import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import CurrencyField from '../../promotions/components/CurrencyField'
import DateTimePickerField from '../../promotions/components/DateTimePickerField'
import type {TopicFormField} from '../data/topicForms'

type Props = {
  field: TopicFormField
  value: unknown
  onChange: (key: string, value: string | string[]) => void
  error?: string
  disabled?: boolean
}

export default function TopicField({field, value, onChange, error, disabled}: Props) {
  const helperText = error || field.help
  const textValue = typeof value === 'string' ? value : ''
  const common = {
    label: field.label,
    value: textValue,
    onChange: (value: string) => onChange(field.key, value),
    required: field.required,
    helperText,
    error: !!error,
    disabled,
  }

  if (field.type === 'money') return <CurrencyField {...common} />
  if (field.type === 'datetime') return <DateTimePickerField {...common} />

  if (field.type === 'radio' || field.type === 'checkbox') {
    const options = field.options || []
    const selected = Array.isArray(value) ? value.map(String) : []
    const allSelected =
      options.length > 0 && options.every((option) => selected.includes(option.value))
    const compact = options.every((option) => option.label.length <= 10)
    const longOptions = options.some((option) => option.label.length > 30)
    const choiceStyle = (checked: boolean) => ({
      m: 0,
      minWidth: 0,
      alignItems: 'center',
      border: '1px solid',
      borderColor: error ? 'error.light' : checked ? 'primary.main' : 'divider',
      bgcolor: checked ? '#eff6ff' : '#fff',
      borderRadius: 1.5,
      px: 0.5,
      py: 0.25,
      transition: 'background-color 150ms, border-color 150ms',
      '&:hover': {bgcolor: disabled ? undefined : '#f8fafc'},
      '& .MuiFormControlLabel-label': {
        fontSize: '13px',
        lineHeight: 1.5,
        py: 0.75,
        pr: 1,
        overflowWrap: 'anywhere',
      },
    })
    return (
      <FormControl
        component='fieldset'
        fullWidth
        error={!!error}
        required={field.required}
        disabled={disabled}
        sx={{minWidth: 0}}
      >
        <FormLabel
          component='legend'
          id={field.key + '-label'}
          sx={{
            fontWeight: 600,
            mb: 1,
            maxWidth: '100%',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {field.label}
        </FormLabel>
        {field.type === 'radio' ? (
          <RadioGroup
            aria-labelledby={field.key + '-label'}
            aria-describedby={helperText ? field.key + '-help' : undefined}
            name={field.key}
            value={textValue}
            onChange={(event) => onChange(field.key, event.target.value)}
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: {
                xs: compact ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                sm: longOptions ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
              },
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size='small' />}
                label={option.label}
                sx={choiceStyle(textValue === option.value)}
              />
            ))}
          </RadioGroup>
        ) : (
          <Box
            role='group'
            aria-labelledby={field.key + '-label'}
            aria-describedby={helperText ? field.key + '-help' : undefined}
          >
            <FormControlLabel
              sx={{
                ml: -1.25,
                mb: 0.75,
                '& .MuiFormControlLabel-label': {fontSize: '12px', color: 'text.secondary'},
              }}
              control={
                <Checkbox
                  size='small'
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={(event) =>
                    onChange(
                      field.key,
                      event.target.checked ? options.map((option) => option.value) : []
                    )
                  }
                />
              }
              label='Marcar todos'
            />
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: compact ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
              }}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  label={option.label}
                  sx={choiceStyle(selected.includes(option.value))}
                  control={
                    <Checkbox
                      size='small'
                      checked={selected.includes(option.value)}
                      onChange={(event) =>
                        onChange(
                          field.key,
                          event.target.checked
                            ? [...selected, option.value]
                            : selected.filter((item) => item !== option.value)
                        )
                      }
                    />
                  }
                />
              ))}
            </Box>
          </Box>
        )}
        {helperText && <FormHelperText id={field.key + '-help'}>{helperText}</FormHelperText>}
      </FormControl>
    )
  }

  return (
    <TextField
      id={field.key}
      fullWidth
      size='small'
      label={field.label}
      value={textValue}
      required={field.required}
      disabled={disabled}
      error={!!error}
      helperText={helperText}
      multiline={field.type === 'textarea'}
      minRows={field.type === 'textarea' ? 4 : undefined}
      placeholder={field.type === 'number' ? 'Digite o novo valor...' : 'Digite aqui...'}
      onChange={(event) => onChange(field.key, event.target.value)}
      inputProps={{
        maxLength: field.maxLength,
        inputMode: field.type === 'number' ? 'decimal' : undefined,
      }}
    />
  )
}
